import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Pagination, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getApi } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import swal from 'sweetalert2';

const Referrals = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [referrals, setReferrals] = useState([]);
    const [stats, setStats] = useState(null);
    const [earnings, setEarnings] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        fetchReferrals();
    }, [auth, navigate, filters]);

    const fetchReferrals = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                ...filters,
                page: filters.page,
                limit: filters.limit
            }).toString();

            const response = await getApi(`referrals?${queryParams}`);
            
            if (response.success) {
                setReferrals(response.data.referrals);
                setStats(response.data.stats);
                setEarnings(response.data.earnings);
                setPagination(response.data.pagination);
            } else {
                throw new Error(response.message || 'Failed to fetch referrals');
            }
        } catch (error) {
            console.error('Error fetching referrals:', error);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset to first page when filter changes
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page
        }));
    };

    if (loading) return <Loading />;

    return (
        <>
            <PageTitle activeMenu="Referrals" motherMenu="Network" />

            <div className="row">
                {/* Stats Cards */}
                <div className="col-xl-3 col-sm-6">
                    <Card className="card-stats mb-4">
                        <Card.Body>
                            <Row>
                                <div className="col">
                                    <h5 className="card-title text-uppercase text-muted mb-0">Total Referrals</h5>
                                    <span className="h2 font-weight-bold mb-0">{stats?.total || 0}</span>
                                </div>
                                <div className="col-auto">
                                    <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                                        <i className="fas fa-users"></i>
                                    </div>
                                </div>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-xl-3 col-sm-6">
                    <Card className="card-stats mb-4">
                        <Card.Body>
                            <Row>
                                <div className="col">
                                    <h5 className="card-title text-uppercase text-muted mb-0">Active Users</h5>
                                    <span className="h2 font-weight-bold mb-0">{stats?.active || 0}</span>
                                </div>
                                <div className="col-auto">
                                    <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                                        <i className="fas fa-user-check"></i>
                                    </div>
                                </div>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-xl-3 col-sm-6">
                    <Card className="card-stats mb-4">
                        <Card.Body>
                            <Row>
                                <div className="col">
                                    <h5 className="card-title text-uppercase text-muted mb-0">KYC Verified</h5>
                                    <span className="h2 font-weight-bold mb-0">{stats?.kycVerified || 0}</span>
                                </div>
                                <div className="col-auto">
                                    <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                                        <i className="fas fa-id-card"></i>
                                    </div>
                                </div>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                {/* <div className="col-xl-3 col-sm-6">
                    <Card className="card-stats mb-4">
                        <Card.Body>
                            <Row>
                                <div className="col">
                                    <h5 className="card-title text-uppercase text-muted mb-0">Total Earnings</h5>
                                    <span className="h2 font-weight-bold mb-0">${earnings?.total || 0}</span>
                                </div>
                                <div className="col-auto">
                                    <div className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow">
                                        <i className="fas fa-dollar-sign"></i>
                                    </div>
                                </div>
                            </Row>
                        </Card.Body>
                    </Card>
                </div> */}
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>My Referrals</Card.Title>
                    <div className="d-flex gap-2">
                        <Form.Select
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                            className="w-auto"
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="blocked">Blocked</option>
                        </Form.Select>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Status</th>
                                    <th>KYC Status</th>
                                    <th>Joined Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.map(referral => (
                                    <tr key={referral._id}>
                                        <td>{referral.name}</td>
                                        <td>{referral.email}</td>
                                        <td>{referral.mobileNumber}</td>
                                        <td>
                                            <Badge bg={
                                                !referral?.status ? 'secondary' :
                                                referral.status === 'active' ? 'success' :
                                                referral.status === 'inactive' ? 'warning' :
                                                'danger'
                                            }>
                                                {(referral?.status || 'UNKNOWN').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={
                                                !referral?.kycStatus ? 'secondary' :
                                                referral.kycStatus === 'approved' ? 'success' :
                                                referral.kycStatus === 'pending' ? 'warning' :
                                                referral.kycStatus === 'rejected' ? 'danger' :
                                                'secondary'
                                            }>
                                                {(referral?.kycStatus || 'NOT SUBMITTED').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>
                                            {referral?.joinedAt ? new Date(referral.joinedAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                                {referrals.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No referrals found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {pagination && pagination.pages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={pagination.page === 1} 
                                />
                                <Pagination.Prev 
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                />
                                
                                {[...Array(pagination.pages)].map((_, index) => (
                                    <Pagination.Item
                                        key={index + 1}
                                        active={pagination.page === index + 1}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </Pagination.Item>
                                ))}
                                
                                <Pagination.Next 
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.pages}
                                />
                                <Pagination.Last 
                                    onClick={() => handlePageChange(pagination.pages)}
                                    disabled={pagination.page === pagination.pages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(Referrals); 